import * as React from 'react';
import {
    ContextActionType,
    ModalContext,
    ModalFormTitle,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FilterOptionValue,
    FiltersDatatableList,
} from '@europrocurement/flexy-datatable';
import { TblTypeDocumentJsonldTypedocumentRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { useDispatch } from 'react-redux';
import {
    addItem,
    checkIcon,
    emptyDocument,
    errorIcon,
    FaOptionIcon,
    NamedIconsType,
    returnedFromTreatmentOrganismDocument,
    toSendByPrescriberDocument,
    warningIcon,
} from '@europrocurement/l2d-icons';
import { documentTypes, findLabelByDocTypeId, useOffreService } from '@europrocurement/l2d-domain';
import { useDefaultContextActions } from '@europrocurement/l2d-modules/hooks';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { TblTypeDocumentJsonldTypedocumentdossierRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import ListingWithSearchAndPreview from '../components/ListingWithSearchAndPreview';
import LabeledIcon from '../components/LabeledIcon';
import Form from './Form';
import { documentStatusOptions } from './options';
import { PossibleTypes } from '../../Societes';

const Panel: React.FunctionComponent = function () {
    const { modalActions } = React.useContext(ModalContext);

    const dispatch = useDispatch();

    const { documentModel: model } = useOffreService();
    const { backContextActions } = useDefaultContextActions();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const reloadList = () => dispatch(model.datasourcesThunks.main.getData({}) as any);

    const listingButton = {
        label: 'Créer un document',
        icon: addItem,
        action: () =>
            modalActions.call(
                <>
                    <ModalFormTitle
                        icon={model.icon}
                        title="Création d'un document"
                    />
                    <Form
                        isCreate
                        afterSubmit={reloadList}
                    />
                </>,
            ),
    };

    const getTypeDocumentIcon = React.useCallback(
        (typeDocument: TblTypeDocumentJsonldTypedocumentdossierRead): NamedIconsType => {
            // Si "Document à envoyer par le prescripteur"
            if (typeDocument.type === 1) {
                return toSendByPrescriberDocument;
            }

            // Si "Document retourné par organisme traitant"
            if (typeDocument.type === 3) {
                return returnedFromTreatmentOrganismDocument;
            }

            return emptyDocument;
        },
        [],
    );

    const getStatus = (statusId: number | null | undefined) => {
        const emptyStatus = {
            label: 'Aucun statut',
            icon: warningIcon,
        };
        if (!statusId) {
            return emptyStatus;
        }
        const status = documentStatusOptions.find((option) => option.value === statusId);
        if (!status) {
            return emptyStatus;
        }

        return status;
    };

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Créer un document',
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: listingButton.action,
            },
            ...backContextActions,
        ],
        [backContextActions, listingButton.action],
    );

    useDumbActionsContext(contextActions);

    const columns: ColumnDatatable<TblTypeDocumentJsonldTypedocumentRead>[] = [
        {
            label: 'Libelle',
            displayLabel: true,
            render: (document) => (
                <Stack>
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {document.libelle}
                    </Typography>
                    <Typography variant="l2dbodyxs">{document.name}</Typography>
                </Stack>
            ),
            isDisplayed: true,
            sortable: true,
            field: 'libelle',
        },
        {
            label: 'Type',
            displayLabel: true,
            render: (typeDocument) => (
                <Tooltip
                    title={
                        typeDocument.type ? findLabelByDocTypeId(typeDocument?.type) : 'Non précisé'
                    }
                >
                    <Box>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            <LabeledIcon icon={getTypeDocumentIcon(typeDocument)} />
                        </Typography>
                    </Box>
                </Tooltip>
            ),
            isDisplayed: true,
            sortable: false,
        },
        {
            label: 'Statut',
            displayLabel: true,
            render: (item) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    <LabeledIcon
                        icon={getStatus(item.statut).icon}
                        label={`${getStatus(item.statut).label}`}
                    />
                </Typography>
            ),
            isDisplayed: true,
            sortable: false,
        },
        {
            label: 'Signable',
            displayLabel: true,
            render: (item) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    <LabeledIcon icon={item.signable ? checkIcon : errorIcon} />
                </Typography>
            ),
            isDisplayed: true,
            sortable: false,
        },
    ];

    const filters: FiltersDatatableList = [
        {
            field: 'libelle',
            label: 'Libelle',
            type: 'text',
        },
        {
            field: 'libelleDF',
            label: "Libelle Démat'Facile",
            type: 'text',
        },
        {
            field: 'signable',
            label: 'Signable',
            type: 'boolean',
        },
        {
            field: 'statut',
            label: 'Statut',
            type: 'staticSelect',
            options: documentStatusOptions as unknown as FilterOptionValue[],
        },
        {
            field: 'type',
            label: 'Type de document',
            type: 'staticSelect',
            options: documentTypes as unknown as FilterOptionValue[],
        },
    ];

    return (
        <ListingWithSearchAndPreview
            listingTitle="Liste des documents"
            listingButton={listingButton}
            listingColumns={columns}
            form={Form}
            model={model}
            filters={filters}
            filtersControl
            searchBarType={PossibleTypes.Document}
            hidePreview
        />
    );
};

export default Panel;
